import Component from '@glimmer/component';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq, gt } from 'tio-ui/utilities';
import {
  PhBrain,
  PhArrowUp,
  PhArrowDown,
  PhTarget,
  PhChartBar,
  PhUsers,
  PhLightning,
} from 'ember-phosphor-icons';
import StatCard from 'tio-reimagined/components/stat-card';

// Mock skills inventory data
const SKILLS_DATA = {
  summary: {
    totalSkills: 450,
    criticalSkills: 28,
    skillsTracked: 892,
    avgProficiency: 72,
  },
  topSkills: [
    { name: 'Patient Care', employees: 892, avgProficiency: 88, trend: 'stable' },
    { name: 'Epic EMR', employees: 756, avgProficiency: 72, trend: 'up' },
    { name: 'Clinical Documentation', employees: 689, avgProficiency: 81, trend: 'stable' },
    { name: 'HIPAA Compliance', employees: 850, avgProficiency: 91, trend: 'stable' },
    { name: 'IV Therapy', employees: 534, avgProficiency: 78, trend: 'up' },
    { name: 'Leadership', employees: 198, avgProficiency: 65, trend: 'up' },
    { name: 'Data Analytics', employees: 134, avgProficiency: 58, trend: 'up' },
  ],
  criticalGaps: [
    { skill: 'Epic EHR Certification', current: 120, needed: 300, gap: 180, priority: 'critical' },
    { skill: 'ICU Specialization', current: 45, needed: 80, gap: 35, priority: 'critical' },
    { skill: 'Leadership & Management', current: 198, needed: 280, gap: 82, priority: 'high' },
    { skill: 'Health Informatics', current: 45, needed: 90, gap: 45, priority: 'high' },
    { skill: 'Data Analytics', current: 134, needed: 200, gap: 66, priority: 'medium' },
  ],
  departmentView: [
    { department: 'Medical-Surgical', employees: 450, avgSkills: 12, avgProficiency: 76, gaps: 3 },
    { department: 'ICU/Critical Care', employees: 180, avgSkills: 15, avgProficiency: 82, gaps: 5 },
    { department: 'Emergency', employees: 220, avgSkills: 14, avgProficiency: 79, gaps: 4 },
    { department: 'Pediatrics', employees: 95, avgSkills: 13, avgProficiency: 77, gaps: 2 },
    { department: 'Surgery', employees: 165, avgSkills: 16, avgProficiency: 84, gaps: 3 },
  ],
};

export default class CareerPathwaysAdminSkillsInventory extends Component {
  get data() {
    return SKILLS_DATA;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 pb-12">
      {{! Header }}
      <div class="flex justify-between items-center mb-6">
        <div>
          <Heading @level={{1}} class="text-2xl font-bold text-gray-900 dark:text-white">
            Skills Inventory
          </Heading>
          <Text @intent="muted">Organization-wide skills visibility and gap analysis</Text>
        </div>
        <div class="flex gap-3">
          <Button @appearance="outlined" @intent="primary">
            <PhChartBar @size={{18}} class="mr-2" />
            Export Report
          </Button>
          <Button @intent="primary">
            <PhTarget @size={{18}} class="mr-2" />
            Configure Priorities
          </Button>
        </div>
      </div>

      {{! Summary Cards }}
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          @icon={{PhBrain}}
          @label="Skills Tracked"
          @value="{{this.data.summary.totalSkills}}"
          @subtext="Unique skills in system"
          @color="violet"
        />
        <StatCard
          @icon={{PhUsers}}
          @label="Employees Profiled"
          @value="{{this.data.summary.skillsTracked}}"
          @subtext="With skills profiles"
          @color="teal"
        />
        <StatCard
          @icon={{PhLightning}}
          @label="Critical Skills"
          @value="{{this.data.summary.criticalSkills}}"
          @subtext="With gaps to close"
          @color="red"
          @valueClass="text-red-600"
        />
        <StatCard
          @icon={{PhChartBar}}
          @label="Avg Proficiency"
          @value="{{this.data.summary.avgProficiency}}%"
          @subtext="Across all skills"
          @color="green"
        />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {{! Critical Gaps }}
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
            <PhLightning @size={{20}} class="text-red-500" />
            <span class="font-medium text-gray-900 dark:text-white">Critical Skills Gaps</span>
          </div>
          <div class="p-5">
            <div class="space-y-4">
              {{#each this.data.criticalGaps as |gap|}}
                <div class="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <div class="flex items-center justify-between mb-2">
                    <Text class="font-semibold text-gray-900 dark:text-white">{{gap.skill}}</Text>
                    {{#if (eq gap.priority "critical")}}
                      <Chip @intent="danger" @size="sm">Critical</Chip>
                    {{else if (eq gap.priority "high")}}
                      <Chip @intent="warning" @size="sm">High</Chip>
                    {{else}}
                      <Chip @intent="default" @size="sm">Medium</Chip>
                    {{/if}}
                  </div>
                  <div class="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>Current: {{gap.current}}</span>
                    <span>Needed: {{gap.needed}}</span>
                    <span class="text-red-600 dark:text-red-400 font-medium">Gap: {{gap.gap}}</span>
                  </div>
                </div>
              {{/each}}
            </div>
          </div>
        </div>

        {{! Top Skills }}
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
            <PhBrain @size={{20}} class="text-violet-600" />
            <span class="font-medium text-gray-900 dark:text-white">Top Skills by Employee Count</span>
          </div>
          <div class="p-5">
            <div class="space-y-3">
              {{#each this.data.topSkills as |skill|}}
                <div
                  class="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
                >
                  <div class="flex-1">
                    <Text class="font-medium text-gray-900 dark:text-white">{{skill.name}}</Text>
                    <Text @intent="muted" @size="sm">{{skill.employees}} employees</Text>
                  </div>
                  <div class="flex items-center gap-4">
                    <div class="text-right">
                      <Text class="font-semibold">{{skill.avgProficiency}}%</Text>
                      <Text @intent="muted" @size="sm">avg</Text>
                    </div>
                    {{#if (eq skill.trend "up")}}
                      <PhArrowUp @size={{18}} class="text-green-500" />
                    {{else if (eq skill.trend "down")}}
                      <PhArrowDown @size={{18}} class="text-red-500" />
                    {{else}}
                      <div class="w-[18px]"></div>
                    {{/if}}
                  </div>
                </div>
              {{/each}}
            </div>
          </div>
        </div>
      </div>

      {{! Department View }}
      <div
        class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mt-6"
      >
        <div class="flex items-center gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
          <PhUsers @size={{20}} class="text-teal-600" />
          <span class="font-medium text-gray-900 dark:text-white">Skills by Department</span>
        </div>
        <div class="p-5">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr
                  class="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700"
                >
                  <th class="pb-3 font-medium">Department</th>
                  <th class="pb-3 font-medium text-center">Employees</th>
                  <th class="pb-3 font-medium text-center">Avg Skills</th>
                  <th class="pb-3 font-medium text-center">Proficiency</th>
                  <th class="pb-3 font-medium text-center">Critical Gaps</th>
                  <th class="pb-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
                {{#each this.data.departmentView as |dept|}}
                  <tr>
                    <td class="py-4">
                      <Text
                        class="font-medium text-gray-900 dark:text-white"
                      >{{dept.department}}</Text>
                    </td>
                    <td
                      class="py-4 text-center text-gray-600 dark:text-gray-400"
                    >{{dept.employees}}</td>
                    <td
                      class="py-4 text-center text-gray-600 dark:text-gray-400"
                    >{{dept.avgSkills}}</td>
                    <td class="py-4 text-center">
                      {{#if (gt dept.avgProficiency 80)}}
                        <Chip @intent="success" @size="sm">{{dept.avgProficiency}}%</Chip>
                      {{else if (gt dept.avgProficiency 70)}}
                        <Chip @intent="warning" @size="sm">{{dept.avgProficiency}}%</Chip>
                      {{else}}
                        <Chip @intent="danger" @size="sm">{{dept.avgProficiency}}%</Chip>
                      {{/if}}
                    </td>
                    <td class="py-4 text-center">
                      {{#if (gt dept.gaps 3)}}
                        <Chip @intent="danger" @size="sm">{{dept.gaps}} gaps</Chip>
                      {{else}}
                        <Chip @intent="default" @size="sm">{{dept.gaps}} gaps</Chip>
                      {{/if}}
                    </td>
                    <td class="py-4 text-right">
                      <Button @appearance="outlined" @size="sm">View Details</Button>
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
